import React from 'react';

import { useClassnames, classnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { ColorBubbleLetter } from '@lumapps/lumx-texts/components/ColorBubbleLetter';
import { REAL_SIZE_FOR_LUMX_SIZE } from '@lumapps/lumx/constants';
import {
    Avatar,
    Size,
    Button,
    Emphasis,
    AvatarSize,
    ButtonProps,
    AvatarProps,
    TooltipProps,
    Tooltip,
} from '@lumapps/lumx/react';
import { mergeRefs } from '@lumapps/utils/react/mergeRefs';

import { DEFAULT_AVATAR } from '../../constants';
import { useUser } from '../../hooks/useUser';
import { BaseUserComponentProps } from '../../types';
import { isUserDeleted } from '../../utils/isUserDeleted';
import { useUserCardPopover } from '../UserCardPopover';
import { UserCardPopoverWrapper } from '../UserCardPopover/Wrapper';

import './index.scss';

export interface UserAvatarProps extends Partial<AvatarProps>, BaseUserComponentProps {
    /** Action when the link is clicked */
    onClick?(): void;
    /** Avatar size. */
    size?: AvatarSize;
    /** whether the avatar should be displayed as an image, as a button or as a link. */
    avatarAs?: 'image' | 'button' | 'link';
    /** converts the avatar into a link */
    linkUrl?: string;
    /** additional props */
    buttonProps?: ButtonProps;
    /** props for a tooltip that will surround the button or link. */
    tooltipProps?: Partial<TooltipProps>;
    /** Whether the default tooltip should be displayed or not */
    showDefaultTooltip?: boolean;
    /** when true, this component will display the component ColorBubbleLetter instead of the avatar */
    useColorBubbleLetter?: boolean;
}

export const CLASSNAME = 'user-avatar';
/**
 * Displays a User's avatar, while managing different types of displays, either as an image (default),
 * a link or a button. Use this component if you need to display a user's avatar in our product.
 */
export const UserAvatar: React.FC<UserAvatarProps> = React.forwardRef<HTMLButtonElement, UserAvatarProps>(
    (props: UserAvatarProps, ref) => {
        const {
            user,
            onClick,
            className,
            scope = 'user-avatar',
            size = Size.m,
            avatarAs = 'image',
            buttonProps,
            linkUrl,
            tooltipProps,
            showUserCardOnHover = false,
            fallbackAvatar = DEFAULT_AVATAR,
            showDefaultTooltip = false,
            useColorBubbleLetter = false,
            userCardOnHoverDelay,
            badge,
            ...forwardedProps
        } = props;

        const { get } = useDataAttributes(scope);
        const { block } = useClassnames(CLASSNAME);

        const isDeleted = isUserDeleted(user);

        const {
            onMouseEnter,
            onMouseLeave,
            user: userProfile,
            isLoading,
            showUserCard,
            asDialog,
            onClick: onUserCardTriggerClick,
            anchorRef,
        } = useUserCardPopover(user, {
            ...forwardedProps,
            delay: userCardOnHoverDelay,
            shouldUseTrigger: true,
            targetClassname: [CLASSNAME],
        });

        const imgProps = {
            width: REAL_SIZE_FOR_LUMX_SIZE[size],
            height: REAL_SIZE_FOR_LUMX_SIZE[size],
        };

        const {
            avatarProps,
            getUserProfilePicture,
            getUserFullName,
            userProfile: userProfileRoute,
        } = useUser(userProfile, forwardedProps);

        const linkUrlToUse = linkUrl || userProfileRoute?.linkProps.href;
        // The UserAvatar can only be an image if it is specifically defined as such and there is no showUserCardOnHover
        // If we need to display the user card on hover, we need to change the image into something focusable.
        const isImage = avatarAs === 'image' && !showUserCardOnHover;
        // The UserAvatar can only be a link if it is specifically defined as such, or the avatarAs is a button and there is no onClick,
        // or if we need to show the user card on hover.
        const isLink = avatarAs === 'link' || (avatarAs === 'button' && !onClick) || showUserCardOnHover;

        let AvatarComponent = (
            // eslint-disable-next-line deprecation/deprecation
            <Avatar
                className={isImage ? classnames(className, block()) : undefined}
                size={size}
                alt={avatarProps.alt}
                {...get({ element: 'avatar', action: !isImage ? 'image' : undefined })}
                {...forwardedProps}
                badge={badge}
                image={getUserProfilePicture()}
                thumbnailProps={{
                    // eslint-disable-next-line deprecation/deprecation
                    fallback: <Avatar image={fallbackAvatar} size={size} alt="" />,
                    ...forwardedProps.imgProps,
                    imgProps,
                }}
            />
        );

        if (useColorBubbleLetter) {
            AvatarComponent = (
                <ColorBubbleLetter
                    size={size}
                    text={getUserFullName()}
                    maxLength={2}
                    isRounded
                    shouldDisplayTextAsVisuallyHidden
                />
            );
        }

        if (isImage || isDeleted) {
            return AvatarComponent;
        }

        let AvatarButtonComponent = (
            <Button
                ref={!asDialog ? mergeRefs([ref, anchorRef as React.RefObject<HTMLButtonElement>]) : undefined}
                className={classnames(
                    className,
                    block({
                        link: isLink,
                        button: !isLink && avatarAs === 'button',
                        image: isImage,
                        'with-badge': Boolean(badge),
                    }),
                )}
                onClick={onClick}
                role={linkUrlToUse && isLink ? 'link' : 'button'}
                emphasis={Emphasis.low}
                // Transforms the button into a proper <a> link with the LumX button style.
                href={isLink ? linkUrlToUse : undefined}
                {...buttonProps}
                style={{
                    ...buttonProps?.style,
                    ...imgProps,
                }}
                onMouseEnter={showUserCardOnHover ? onMouseEnter : undefined}
                onMouseLeave={onMouseLeave}
                target="_self"
                {...get({ element: 'avatar' })}
            >
                {AvatarComponent}
            </Button>
        );

        if (showUserCardOnHover) {
            AvatarButtonComponent = (
                <UserCardPopoverWrapper
                    showUserCardOnHover={showUserCardOnHover}
                    anchorRef={anchorRef}
                    showUserCard={showUserCard}
                    onClick={onUserCardTriggerClick}
                    onMouseLeave={onMouseLeave}
                    onMouseEnter={onMouseEnter}
                    user={userProfile}
                    isLoading={isLoading}
                    asDialog={asDialog}
                    {...forwardedProps}
                    userBlockProps={{
                        ...forwardedProps.userBlockProps,
                        avatarProps: {
                            thumbnailProps: {
                                ...forwardedProps?.userBlockProps?.avatarProps?.thumbnailProps,
                                // eslint-disable-next-line deprecation/deprecation
                                fallback: <Avatar size={Size.l} image={fallbackAvatar} alt="" />,
                                ...avatarProps?.thumbnailProps,
                            },
                        },
                    }}
                >
                    {AvatarButtonComponent}
                </UserCardPopoverWrapper>
            );
        }

        if (!tooltipProps && !showDefaultTooltip) {
            return AvatarButtonComponent;
        }

        return (
            <Tooltip label={getUserFullName()} {...tooltipProps}>
                {AvatarButtonComponent}
            </Tooltip>
        );
    },
);
